import { buildScheduleCheckLabel, CheckDTO, formatDateWithUserTimezone, formatReadingValue, GetChecksResponseItemDTO } from '@gearbelt/global';

import { useUserStore } from '@/stores/user.store';

export { addCalendarInterval } from '@gearbelt/global';

/**
 * Formats a schedule check label for display in tables.
 * Used when the check already has the label and schedule embedded (e.g., GetChecksResponseItemDTO).
 * Returns format: "Check A - 500 km or 30 days" or just "Check A" if no triggers.
 * For fixed anchors with a baseDate, shows the resolved date (e.g., "Check A - 15 Mar 2026").
 */
export function formatScheduleCheckLabel(check: GetChecksResponseItemDTO | CheckDTO): string | null {
  const userTimezone = useUserStore.getState().user?.timezone;
  return buildScheduleCheckLabel({
    label: check.scheduleCheck?.label,
    triggers: (check.schedule?.triggers ?? []).map((t) => ({
      calendarInterval: t.calendarInterval,
      unitInterval: t.unitInterval,
      unitAbbreviation: t.unitAbbreviation,
      anchorType: t.anchorType,
      baseDate: t.baseDate,
      fixedTriggerType: t.fixedTriggerType,
    })),
    occurrenceNumber: check.occurrenceNumber,
    formatDate: (d) => formatDateWithUserTimezone(d, userTimezone),
  });
}

/**
 * Joins a check's current asset readings (one per unit-based trigger) for display,
 * e.g. "1,250 km / 480 hrs". Calendar-based summary rows carry no reading and are skipped.
 * Returns null when the check has no unit reading (calendar-only or none recorded).
 *
 * `currentAssetReading` arrives as a raw "<numeric> <abbreviation>" string (the DB
 * `numeric` value is un-separated, e.g. "1250.00 km"); the leading number is reformatted
 * with toLocaleString so the value reads "1,250 km", matching the Due Reading column.
 */
export function formatLastReading(check: GetChecksResponseItemDTO): string | null {
  const readings = check.summary
    .map((s) => s.currentAssetReading)
    .filter((reading): reading is string => reading != null)
    .map((reading) => formatReadingValue(reading));
  return readings.length > 0 ? readings.join(' / ') : null;
}
